<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- Nom du forfait -->
            <CustomInput
                :label="$t('treatment.step1.content')"
                :required="true"
                v-model="formData.name"
                :placeholder="$t('treatment.step1.namePlaceholder')"
                :state="getState('name')"
                :errorText="errors.name"
                @input="clearError('name')"
                @focus="activeField = 'name'"
                @blur="activeField = null"
            />

            <!-- Description -->
            <BaseTextarea
                :label="$t('treatment.step1.descriptionLabel')"
                v-model="formData.description"
                :placeholder="$t('treatment.step1.descriptionPlaceholder')"
                :useEditor="true"
                @focus="setActiveField('description')"
                @blur="clearActiveField('description')"
                iconIA
                :rows="5"
                :state="getState('description')"
                :error="errors.description"
                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                customLabelClass="poppinsLight ppv2-text-black"
                classContainerAdd="ppv2-mb-[20px]"
                :required="false"
            />

            <!-- Image Upload -->
            <div class="upload-container">
                <CustomInputUpload
                    :label="$t('treatment.step1.illustrationLabel')"
                    v-model="formData.image"
                    :required="false"
                    :state="getState('image')"
                    :errorText="errors.image"
                    @input="onFileSelected"
                    @focus="activeField = 'image'"
                    @blur="activeField = null"
                />
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': complited }">
                <BackButton v-show="!complited" type="button" @click="handleBackClick" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ complited ? $t('common.save') : $t('experience.next') }}
                </BaseButton>
            </div>
        </div>

        <!-- Exit Popup -->
        <CustomPopup v-model="showExitPopup" :defaultTitle="popupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="exit()"
                    :customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isSaveBtn="true"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="button"
                    variant="primary"
                    @click="showExitPopup = false"
                    :customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isSaveBtn="true"
                    :isLargeBtn="true"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomPopup>

        <!-- Preview Panel -->
        <PreviewTreatment :activeField="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import HeaderStep from './HeaderStep.vue'
import PreviewTreatment from './PreviewTreatment.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import CustomPopup from '../../../Shared/CustomDialog.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'TreatmentStep1',
    components: {
        HeaderStep,
        PreviewTreatment,
        CustomInput,
        BaseTextarea,
        CustomInputUpload,
        BaseButton,
        BackButton,
        CustomPopup,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        complited: { type: [Boolean], default: false },
    },
    data() {
        return {
            activeField: null,
            errors: {},
            showExitPopup: false,
            popupTitle: this.$t('experience.titleClosePopup'),
            initialDataString: null,
        }
    },
    computed: {
        ...mapGetters('treatment', ['formData', 'isEditMode', 'isDirty', 'loading']),
        formData: {
            get() {
                return this.$store.getters['treatment/formData']
            },
            set(value) {
                this.$store.dispatch('treatment/setFormData', value)
            },
        },
    },
    mounted() {
        this.initialDataString = this.getFormDataString()
    },
    methods: {
        normalizeFontSize,
        ...mapActions('treatment', ['setFormData', 'saveTreatment']),
        normalizeSize,

        clearError(field) {
            if (this.errors[field]) {
                delete this.errors[field]
            }
        },

        setActiveField(field) {
            this.activeField = field
        },

        clearActiveField(field) {
            if (this.activeField === field) {
                this.activeField = null
            }
        },

        onFileSelected(file) {
            if (!file) return

            const reader = new FileReader()
            reader.onload = () => {
                this.setFormData({
                    image: reader.result,
                    imagePreview: reader.result,
                })
            }
            reader.readAsDataURL(file)
        },

        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.formData[field]) return 'valid'
            return 'default'
        },

        getFormDataString() {
            return JSON.stringify({
                name: this.formData.name,
                description: this.formData.description,
                image: this.formData.image,
            })
        },

        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },

        revertChanges() {
            if (!this.initialDataString) return
            const data = JSON.parse(this.initialDataString)
            this.$store.dispatch('treatment/setFormData', {
                name: data.name,
                description: data.description,
                image: data.image,
            })
        },

        handleBackClick() {
            if (this.hasChanges()) {
                this.showExitPopup = true
            } else {
                this.exit()
            }
        },

        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },

        async submitForm() {
            this.errors = {}

            // Local validation
            if (!this.formData.name || !this.formData.name.trim()) {
                this.errors.name = this.$t('treatment.step1.nameRequired')
                return
            }

            // Save to API with error handling
            try {
                await this.saveTreatment()
                this.initialDataString = this.getFormDataString()
                this.$toast.success(this.$t('treatment.notifications.success_update'))
                if (!this.complited) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Display field-specific validation errors if present
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                }
            }
        },

        async saveStep(redirect = false) {
            try {
                // Utilisé pour "Enregistrer et quitter" : on effectue une sauvegarde complète
                await this.$store.dispatch('treatment/saveTreatment')
                this.initialDataString = this.getFormDataString()
                if (redirect) {
                    this.exit()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving treatment:', error)
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
